
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { ArrowRight, CheckCircle, Calendar, Users, Building, Target, TrendingUp, Shield, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { caseStudies } from "@/data/caseStudyData";
import { RedactedCompanyName } from "@/components/case-studies/RedactedCompanyName";
import { BreadcrumbNav } from "@/components/case-studies/BreadcrumbNav";
import { useEffect } from "react";

const TechCompanyInfrastructureScaling = () => {
  const caseStudy = caseStudies.find(study => study.id === "tech-company-infrastructure-scaling");
  const companyName = caseStudy?.redactedName || "Velocity Software Solutions";
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  if (!caseStudy) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="text-center py-20">
              <h1 className="text-2xl font-bold text-gray-900">Case Study Not Found</h1>
              <p className="mt-4 text-gray-600">The case study you're looking for could not be found.</p>
              <Link to="/case-studies" className="mt-8 inline-block">
                <Button>
                  View All Case Studies
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="py-12 md:py-24 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <BreadcrumbNav currentPage="Software Company M365 Transformation" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="mb-6">
                  <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    Case Study: {caseStudy.industry}
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  How <RedactedCompanyName companyName={companyName} /> Transforms Their Microsoft 365 Environment
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Learn how we helped a growing software company transform their Microsoft 365 environment from a vulnerability into a strategic asset.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/get-started">
                    <Button 
                      size="lg"
                      className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 shadow-md group"
                    >
                      Get Similar Results
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <a href="#challenge">
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-purple-300 text-purple-800 hover:bg-purple-50"
                    >
                      Read the Case Study
                    </Button>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <Card className="bg-white border-0 shadow-xl rounded-xl overflow-hidden">
                  <div className="p-6 md:p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Industry</p>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">{caseStudy.industry}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Company Size</p>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">{caseStudy.companyType}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Project Duration</p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">3 Months</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Focus Areas</p>
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Microsoft 365 Optimization</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-gray-100">
                      <h3 className="font-semibold text-gray-900 mb-3">Key Results:</h3>
                      <div className="space-y-2">
                        {caseStudy.results.map((result, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle className="h-3 w-3 text-green-600" />
                            </div>
                            <p className="text-gray-700">{result.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge Section */}
        <section id="challenge" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium mb-4">
                  The Challenge
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Security Vulnerabilities and Inefficient Collaboration
                </h2>
                <p className="text-lg text-gray-600">
                  <RedactedCompanyName companyName={companyName} /> was facing significant challenges with their inconsistent Microsoft 365 environment as they scaled rapidly.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Vulnerabilities</h3>
                  <p className="text-gray-600 mb-4">
                    Multiple security gaps were identified in their tenant but remained unaddressed, putting sensitive data at risk.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Low Microsoft Secure Score of 38 points</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Collaboration Barriers</h3>
                  <p className="text-gray-600 mb-4">
                    Employees were using shadow IT solutions due to collaboration barriers in Teams and disorganized information architecture.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Disorganized information architecture causing data silos</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Inconsistent Configuration</h3>
                  <p className="text-gray-600 mb-4">
                    Inconsistent configuration standards caused frequent user issues and IT support tickets, hampering productivity.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">High volume of IT support tickets for basic issues</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Scaling Challenges</h3>
                  <p className="text-gray-600 mb-4">
                    Company growth was outpacing their ability to properly manage user onboarding/offboarding and security governance.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Manual provisioning processes causing delays and errors</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 p-8 rounded-xl border border-red-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-red-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Breaking Point
                    </h3>
                    <p className="text-gray-700">
                      "We had a mess of Microsoft 365 tools that were theoretically powerful but practically frustrating. Different teams had different ways of working, security was inconsistent, and we were spending too much time fighting with our tools instead of using them to grow our business."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— VP of Engineering at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Solution Section */}
        <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                  The Solution
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Comprehensive Microsoft 365 Optimization
                </h2>
                <p className="text-lg text-gray-600">
                  Our approach focused on a three-phase strategy to fully leverage their Microsoft 365 investment while improving security and collaboration.
                </p>
              </div>
              
              <div className="space-y-8 mb-12">
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Tenant Security Assessment & Remediation</h3>
                      <p className="text-gray-600 mb-4">
                        We conducted a thorough security assessment, identifying and remediating 83 security gaps across identity, data, device, and application layers.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Implemented Microsoft Defender for Endpoint, Identity, Office 365, and Cloud Apps</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Secured identity with conditional access policies and MFA enforcement</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Deployed data loss prevention policies to protect sensitive information</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Target className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Information Architecture Redesign</h3>
                      <p className="text-gray-600 mb-4">
                        We restructured SharePoint sites, Teams channels, and OneDrive storage with consistent permissions models and clearly defined ownership.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Created standardized Teams structure aligned with organizational departments</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Implemented consistent document management with metadata and retention policies</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Enabled secure external sharing with appropriate governance controls</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Automation & End-User Enablement</h3>
                      <p className="text-gray-600 mb-4">
                        We built automated workflows for user provisioning/deprovisioning and delivered targeted training sessions for different departments.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Created automated user lifecycle management with Power Automate</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Developed role-based training program with targeted adoption materials</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Established Change Champions program for ongoing support and feedback</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {caseStudy.testimonial && (
                <div className="bg-purple-50 p-8 rounded-xl border border-purple-100">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-full bg-white border border-purple-200 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        The Transformation
                      </h3>
                      <p className="text-gray-700">
                        {caseStudy.testimonial.quote}
                      </p>
                      <p className="text-sm font-medium text-gray-700 mt-2">— {caseStudy.testimonial.author}, {caseStudy.testimonial.role}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
                  The Results
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Enhanced Security, Collaboration, and Efficiency
                </h2>
                <p className="text-lg text-gray-600">
                  Within three months, <RedactedCompanyName companyName={companyName} /> achieved significant improvements in security posture, collaboration, and operational efficiency.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">353%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Security Score Improvement</h3>
                  <p className="text-gray-600">
                    Microsoft Secure Score increased from 38 to 172 points, dramatically reducing overall organizational risk.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">76%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">IT Support Reduction</h3>
                  <p className="text-gray-600">
                    Reduced user-reported IT issues by 76% through improved system reliability and standardized configurations.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">43%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Collaboration Increase</h3>
                  <p className="text-gray-600">
                    Teams usage increased by 43% after information architecture redesign, eliminating shadow IT usage.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">32%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Cost Savings</h3>
                  <p className="text-gray-600">
                    Achieved 32% savings on security tools through consolidation into the Microsoft Defender suite.
                  </p>
                </div>
              </div>
              
              <div className="bg-green-50 p-8 rounded-xl border border-green-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-green-200 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Long-Term Impact
                    </h3>
                    <p className="text-gray-700">
                      "Our Microsoft 365 environment was holding back our growth. With edgefi optimizing our systems, our team can focus entirely on building products that customers love instead of fighting with inconsistent tools and unreliable security."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— VP of Engineering at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                Ready to Optimize Your Microsoft 365 Environment?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Schedule a consultation to discover how we can help your organization achieve similar results with your existing Microsoft 365 licenses.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link to="/get-started">
                  <Button 
                    size="lg"
                    className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 transition-colors px-8 py-6 h-auto shadow-lg hover:shadow-xl group"
                  >
                    Schedule Your Consultation
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/services/microsoft-365-security">
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 px-8 py-6 h-auto"
                  >
                    Explore Microsoft 365 Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TechCompanyInfrastructureScaling;
